import { TextField } from "@kobalte/core/text-field";
import { getIconComponent } from "@storytell/bsky-icons";
import {
  CreateViewRequestConfig,
  DeleteViewRequestConfig,
  UpdateViewRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, ConceptId, FilterOperator, Label, LabelId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbArrowRight, TbEdit, TbFilterPlus, TbHelpCircle, TbTrash, TbX } from "solid-icons/tb";
import { For, Match, type Setter, Show, Switch, batch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useWire } from "~/wire";
import { TbStackPlus } from "../knowledge/TbStackPlus";
import { LabelPicker } from "../labels";
import { isLightColor } from "../layouts/minimal/isLightColor";
import { DeleteViewModal } from "./DeleteViewModal";
import {
  type FilterBuilder,
  type UIFilterGroup,
  isPositiveOperator,
  serverFiltersToUiFilters,
  uiFiltersToServerFilters,
} from "./filterBuilder";

export const Filters = (props: {
  builder: FilterBuilder<Asset | Concept>;
  hasViewChanges: boolean;
  editView: boolean;
  setEditView: Setter<boolean>;
  activeView: ViewId | "all" | "new";
}) => {
  const wire = useWire();
  const [viewName, setViewName] = createSignal("");
  const [search, setSearch] = createSignal("");
  const filterLabelsBySearch = () => wire.labels.search(search());

  const activeProject = () => {
    const userContext = wire.userContext.get();
    if (!userContext) return undefined;
    return wire.projects.get(userContext.projectId);
  };

  const getUsedLabelIds = () => {
    const usedIds = new Set<LabelId>();
    props.builder.state.filter.groups.forEach((group) => {
      group.filters.forEach((filter) => {
        if (filter.kind === "label") {
          usedIds.add(filter.id as LabelId);
        }
      });
    });
    return usedIds;
  };

  const getAvailableLabels = () => {
    const usedIds = getUsedLabelIds();
    return filterLabelsBySearch().filter((label) => !usedIds.has(label.id));
  };

  const hasAvailableLabels = () => getAvailableLabels().length > 0;

  const addLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.addFilter(
      {
        filter: (entity) =>
          entity.id.startsWith("asset_")
            ? wire.rels.assetHasLabel(entity.id as AssetId, labelId as LabelId)
            : wire.rels.conceptHasLabel(entity.id as ConceptId, labelId as LabelId),
        id: labelId as LabelId,
        kind: "label",
      },
      groupIndex,
    );
  };

  const removeLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.removeFilter(labelId, groupIndex);
  };

  const operator = () => props.builder.state.filter.operator;

  const activeOption = () =>
    props.builder.state.filter.groups.length <= 1 ? undefined : operator() === "and" ? (
      <>
        match <span class="font-bold">all</span> filters
      </>
    ) : (
      <>
        match <span class="font-bold">any</span> filter
      </>
    );

  const [open, setOpen] = createSignal(false);
  const [showDeleteModal, setShowDeleteModal] = createSignal(false);

  const [tempData, setTempData] = createSignal({
    selectedLabels: [] as LabelId[],
  });

  // Initialize viewName with existing view data when editing
  createEffect(() => {
    if (props.editView && viewData()?.name) {
      setViewName(viewData()!.name);
    }
  });

  // Ensure filters are loaded when entering edit mode
  createEffect(() => {
    if (props.editView && props.activeView !== "new" && props.activeView !== "all") {
      const view = wire.views.get(props.activeView);
      if (view?.filters) {
        const filters = serverFiltersToUiFilters<Asset | Concept>(wire, view.filters);
        props.builder.setState({ filter: filters });
      }
    }
  });

  // Reset viewName when switching to new view
  createEffect(() => {
    if (props.activeView === "new") {
      setViewName("");
    }
  });

  let lastView = null as ViewId | "all" | "new" | null;
  createEffect((last) => {
    const view = props.activeView;
    lastView = (last ?? null) as ViewId | "all" | "new" | null;
    return view;
  });

  const viewData = () => {
    if (props.activeView === "new" || props.activeView === "all") return null;
    return wire.views.get(props.activeView);
  };

  const updateView = async (viewId: ViewId, name?: string) => {
    const client = useStorytellClient();
    const request = UpdateViewRequestConfig.withBearerToken(wire.auth.token());
    const response = await client.fetch(
      request,
      { id: viewId },
      {
        id: viewId,
        name: name || wire.views.get(viewId)?.name || "",
        description: "",
        filters: uiFiltersToServerFilters(props.builder.state.filter),
      },
    );

    if (response.isOk()) {
      wire.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
      props.setEditView(false);
    }
  };

  const createView = async (name: string) => {
    try {
      const client = useStorytellClient();
      const request = CreateViewRequestConfig.withBearerToken(wire.auth.token());

      const response = await client.fetch(request, undefined, {
        name: name,
        description: "",
        filters: uiFiltersToServerFilters(props.builder.state.filter),
      });

      if (response.isOk()) {
        wire.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
        wire.knowledgeUi.setActiveView(response.value.id);
      } else {
        console.error("Failed to create view:", response.error);
      }
    } catch (error) {
      console.error("Error creating view:", error);
    }
  };

  const formId = "create-new-view-form";
  const onSubmit = async () => {
    const name = viewName().trim();
    console.log("onSubmit called with name:", name);
    console.log("viewName signal:", viewName());
    console.log("activeView:", props.activeView);
    console.log("editView:", props.editView);

    if (!name) {
      console.log("No name provided, returning");
      return;
    }

    if (props.editView && props.activeView !== "new" && props.activeView !== "all") {
      console.log("Updating existing view");
      await updateView(props.activeView, name);
      return;
    }

    console.log("Creating new view");
    await createView(name);
  };

  const handleDeleteView = async () => {
    if (props.activeView === "new" || props.activeView === "all") return;

    try {
      const client = useStorytellClient();
      const request = DeleteViewRequestConfig.withBearerToken(wire.auth.token());
      const resp = await client.fetch(request, { id: props.activeView as ViewId }, undefined);

      if (resp.isOk()) {
        // Switch to "all" view first
        wire.knowledgeUi.setActiveView("all");
        // Remove the view from local state
        wire.views.remove(props.activeView as ViewId);
        // Trigger a refresh of views to sync with server
        dispatchGlobalEvent("refresh-sync", undefined);
        setShowDeleteModal(false);
      } else {
        console.error("Failed to delete view:", resp.error);
        // Keep modal open on error so user can try again
      }
    } catch (error) {
      console.error("Error deleting view:", error);
      // Keep modal open on error so user can try again
    }
  };

  const currentViewName = () => {
    if (props.activeView === "new" || props.activeView === "all") return "";
    return wire.views.get(props.activeView as ViewId)?.name || "";
  };

  return (
    <div class="flex flex-col gap-2">
      <Show when={props.activeView === "new"}>
        <div class="mb-4 p-4 border-2 border-brand/30 rounded-xl bg-canvas">
          <div class="flex items-start justify-between gap-4 mb-1">
            <h3 class="text-lg font-medium text-primary-text">Create a New Data Collection</h3>
            <a
              href="https://go.storytell.ai/learn-collections"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 underline text-brand text-xs shrink-0"
            >
              <TbHelpCircle />
              Learn about Collections
            </a>
          </div>
          <p class="text-sm text-secondary-text mb-4">
            <span class="font-semibold">Collections let you use Labels to create "fences" around your data.</span>
            <br />
            Collections are subsets of{" "}
            <code class="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs font-bold text-indigo-600 dark:text-indigo-400">
              All Knowledge
            </code>{" "}
            that allow you to chat with just the fenced data, instead of all of it.
          </p>

          <form
            id={formId}
            class="w-full flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {(() => {
              onMount(() => {
                const input = document.getElementById("view-name") as HTMLInputElement;
                input.focus();
              });
              return null;
            })()}
            <div class="flex gap-2">
              <label for="view-name" class="mt-1 text-sm text-secondary-text">
                Collection Name
              </label>
              <TextField required>
                <TextField.Input
                  value={props.activeView === "new" ? viewName() : viewData()?.name || viewName()}
                  id="view-name"
                  name="viewName"
                  class="w-full px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder=""
                  onInput={(e) => setViewName(e.currentTarget.value)}
                />
              </TextField>
            </div>

            <div class="flex items-center justify-between w-full">
              <div class="flex items-center gap-2">
                <Show when={activeOption()}>
                  {(option) => (
                    <button
                      type="button"
                      class="text-xs border flex items-center gap-1 border-primary-border rounded-md px-1.5 py-1 hover:bg-panel-hover cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        props.builder.changeGlobalOperator(operator() === "and" ? "or" : "and");
                      }}
                    >
                      {option()}
                      <TbArrowRight class="stroke-2" />
                    </button>
                  )}
                </Show>
                <For each={props.builder.state.filter.groups}>
                  {(group, groupIndex) => (
                    <>
                      {groupIndex() > 0 && (
                        <span class="text-xs text-secondary-text font-medium px-1">
                          {operator() === "and" ? "and" : "or"}
                        </span>
                      )}
                      <FilterGroup
                        onRemove={() => props.builder.removeFilterGroup(groupIndex())}
                        onSelectOperator={(op) => {
                          props.builder.changeGroupOperator(op, groupIndex());
                        }}
                        onSelectLabel={(l) => addLabelFilter(l.id, groupIndex())}
                        onDeselectLabel={(l) => removeLabelFilter(l.id, groupIndex())}
                        group={group}
                        groupIndex={groupIndex()}
                      />
                    </>
                  )}
                </For>
              </div>
            </div>

            <Show when={hasAvailableLabels()}>
              <div class="flex items-center gap-2 w-full mt-2">
                <span class="text-sm text-secondary-text">
                  {props.builder.state.filter.groups.length > 0
                    ? "Do you have more Labels you want to include or exclude?"
                    : "Select the Labels you want to use for this Collection:"}
                </span>
                <StPopover
                  contentProps={{ class: "z-50" }}
                  placement="bottom-start"
                  noArrow
                  gutter={10}
                  open={open()}
                  onOpenChange={(open) => {
                    setOpen(open);
                    setTempData({ selectedLabels: [] });
                    requestAnimationFrame(() => {
                      if (!open) {
                        if (props.builder.state.filter.groups.at(-1)?.filters.length === 0) {
                          props.builder.removeFilterGroup(props.builder.state.filter.groups.length - 1);
                        }
                      }
                    });
                  }}
                  content={
                    <LabelPicker
                      title="Choose Labels to Filter"
                      disableCreate
                      search={search()}
                      setSearch={setSearch}
                      labels={getAvailableLabels()}
                      selectedLabels={tempData().selectedLabels}
                      onSelectLabel={(l) => {
                        batch(() => {
                          if (tempData().selectedLabels.length === 0) {
                            props.builder.addFilterGroup("and", []);
                          }
                          setTempData((d) => ({ ...d, selectedLabels: [...d.selectedLabels, l.id] }));
                          addLabelFilter(l.id, props.builder.state.filter.groups.length - 1);
                        });
                      }}
                      onDeselectLabel={(l) => {
                        batch(() => {
                          setTempData((d) => ({ ...d, selectedLabels: d.selectedLabels.filter((id) => id !== l.id) }));
                          removeLabelFilter(l.id, props.builder.state.filter.groups.length - 1);
                          if (tempData().selectedLabels.length === 0) {
                            props.builder.removeFilterGroup(props.builder.state.filter.groups.length - 1);
                          }
                        });
                      }}
                      onCreateLabel={async () => {}}
                    />
                  }
                >
                  <StPopoverTrigger
                    as={StButton}
                    icon={TbFilterPlus}
                    noBorder
                    iconClass="size-3.5 stroke-[2.5px]"
                    aria-label="Add Filter"
                    class="group"
                  >
                    <span class="group-hover:hidden">Add a Filter</span>
                    <span class="hidden group-hover:inline">Filter Labels</span>
                  </StPopoverTrigger>
                </StPopover>
              </div>
            </Show>
          </form>

          <div class="flex items-center justify-end gap-2 mt-4">
            <StButton
              noBorder
              noColor
              class="py-1"
              onClick={() => {
                console.log("cancel", lastView);
                if (lastView) wire.knowledgeUi.setActiveView(lastView);
                else wire.knowledgeUi.setActiveView("all");
              }}
            >
              Cancel
            </StButton>
            <StButton
              noBorder
              noColor
              class={`py-1 border-0 ${
                !viewName().trim() || props.builder.state.filter.groups.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-brand hover:bg-brand-hover text-on-brand"
              }`}
              icon={TbStackPlus}
              type="submit"
              form={formId}
              disabled={!viewName().trim() || props.builder.state.filter.groups.length === 0}
            >
              Create Collection
            </StButton>
          </div>
        </div>
      </Show>
      <Show when={props.editView && props.activeView !== "all"}>
        <div class="mb-4 p-4 border-2 border-brand/30 rounded-xl bg-canvas">
          <div class="flex items-start justify-between gap-4 mb-1">
            <h3 class="text-lg font-medium text-primary-text">Edit Collection</h3>
            <a
              href="https://go.storytell.ai/learn-collections"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 underline text-brand text-xs shrink-0"
            >
              <TbHelpCircle />
              Learn about Collections
            </a>
          </div>
          <p class="text-sm text-secondary-text mb-4">Update the name and filters for this data view.</p>

          <form
            id={formId}
            class="w-full flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {(() => {
              onMount(() => {
                const input = document.getElementById("view-name") as HTMLInputElement;
                input.focus();
              });
              return null;
            })()}
            <div class="flex gap-2">
              <label for="view-name" class="mt-1 text-sm text-secondary-text">
                Collection Name
              </label>
              <TextField required>
                <TextField.Input
                  value={props.activeView === "new" ? viewName() : viewData()?.name || viewName()}
                  id="view-name"
                  name="viewName"
                  class="w-full px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder=""
                  onInput={(e) => setViewName(e.currentTarget.value)}
                />
              </TextField>
            </div>

            <div class="flex items-center justify-between w-full">
              <div class="flex items-center gap-2 flex-wrap">
                <Show when={activeOption()}>
                  {(option) => (
                    <button
                      type="button"
                      class="text-xs border flex items-center gap-1 border-primary-border rounded-md px-1.5 py-1 hover:bg-panel-hover cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        props.builder.changeGlobalOperator(operator() === "and" ? "or" : "and");
                      }}
                    >
                      {option()}
                      <TbArrowRight class="stroke-2" />
                    </button>
                  )}
                </Show>
                <For each={props.builder.state.filter.groups}>
                  {(group, groupIndex) => (
                    <>
                      {groupIndex() > 0 && (
                        <span class="text-xs text-secondary-text font-medium px-1">
                          {operator() === "and" ? "and" : "or"}
                        </span>
                      )}
                      <FilterGroup
                        onRemove={() => props.builder.removeFilterGroup(groupIndex())}
                        onSelectOperator={(op) => {
                          props.builder.changeGroupOperator(op, groupIndex());
                        }}
                        onSelectLabel={(l) => addLabelFilter(l.id, groupIndex())}
                        onDeselectLabel={(l) => removeLabelFilter(l.id, groupIndex())}
                        group={group}
                        groupIndex={groupIndex()}
                      />
                    </>
                  )}
                </For>

                <Show when={hasAvailableLabels()}>
                  <StPopover
                    contentProps={{ class: "z-50" }}
                    placement="bottom-start"
                    noArrow
                    gutter={10}
                    open={open()}
                    onOpenChange={(open) => {
                      setOpen(open);
                      setTempData({ selectedLabels: [] });
                      requestAnimationFrame(() => {
                        const input = document.getElementById("label-search") as HTMLInputElement;
                        input?.focus();
                      });
                    }}
                    content={
                      <LabelPicker
                        title="Add a filter"
                        search={search()}
                        setSearch={setSearch}
                        labels={getAvailableLabels()}
                        onSelectLabel={(label) => {
                          addLabelFilter(label.id, props.builder.state.filter.groups.length - 1);
                          setOpen(false);
                        }}
                        onDeselectLabel={() => {}}
                        onClose={() => setOpen(false)}
                        selectedLabels={[]}
                        onCreateLabel={async () => {}}
                      />
                    }
                  >
                    <StPopoverTrigger
                      as={StButton}
                      icon={TbFilterPlus}
                      noBorder
                      iconClass="size-3.5 stroke-[2.5px]"
                      aria-label="Add Filter"
                      class="group"
                    >
                      <span class="group-hover:hidden">Add a Filter</span>
                      <span class="hidden group-hover:inline">Filter Labels</span>
                    </StPopoverTrigger>
                  </StPopover>
                </Show>
              </div>
            </div>
          </form>

          <div class="flex items-center justify-end gap-2 mt-4">
            <StButton
              noBorder
              noColor
              class="py-1"
              onClick={() => {
                props.setEditView(false);
              }}
            >
              Cancel
            </StButton>
            <StButton
              noBorder
              noColor
              class={`py-1 border-0 ${
                !viewName().trim() || props.builder.state.filter.groups.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-brand hover:bg-brand-hover text-on-brand"
              }`}
              icon={TbStackPlus}
              type="submit"
              form={formId}
              disabled={!viewName().trim() || props.builder.state.filter.groups.length === 0}
            >
              Save Changes
            </StButton>
          </div>
        </div>
      </Show>

      <Show when={props.activeView !== "new" && props.activeView !== "all" && !props.editView}>
        <div class="flex items-center gap-2">{/* Filter Labels button moved to NavigationAssetsPane */}</div>

        <div class="flex items-center gap-2">
          {/* Clear and Save as View buttons moved to NavigationAssetsPane */}

          {/* Chat with text */}
          <Show when={props.activeView !== "new" && props.activeView !== "all"}>
            <button
              type="button"
              onClick={() => {
                dispatchGlobalEvent("add-items-to-prompt-scope", {
                  ids: props.activeView !== "all" ? [props.activeView as ViewId] : [],
                  allKnowledge: props.activeView === "all",
                });
              }}
              class="text-sm mr-1 text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border rounded-lg border-primary-border transition-all hover:bg-panel-hover"
            >
              Chat with the{" "}
              <span class="text-transparent bg-gradient-to-r from-violet-600 to-blue-600 dark:from-violet-400 dark:to-blue-400 bg-clip-text">
                @{wire.views.get(props.activeView as ViewId)?.name}
              </span>{" "}
              Collection
              <Show when={activeProject()?.name}>
                {" "}
                in <span class="font-bold">{activeProject()?.name}</span>
              </Show>
            </button>
          </Show>

          {/* Edit and Delete buttons for existing views */}
          <Show when={props.activeView !== "new" && props.activeView !== "all" && !props.editView}>
            <StButton
              class="text-secondary-text mr-1 group"
              noBorder
              onClick={() => {
                props.setEditView(true);
              }}
            >
              <span class="group-hover:hidden">
                <TbEdit class="size-3.5" />
              </span>
              <span class="hidden group-hover:inline">Edit this Collection</span>
            </StButton>
            <StButton
              onClick={() => {
                setShowDeleteModal(true);
              }}
              class="text-secondary-text group -ml-2.5"
              noBorder
            >
              <span class="group-hover:hidden">
                <TbTrash class="size-3.5" />
              </span>
              <span class="hidden group-hover:inline">Delete this Collection</span>
            </StButton>
          </Show>
        </div>
      </Show>

      <DeleteViewModal
        isOpen={showDeleteModal()}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteView}
        viewName={currentViewName()}
      />
    </div>
  );
};

export const FilterGroup = (props: {
  group: UIFilterGroup<Asset | Concept>;
  groupIndex: number;
  onSelectLabel: (label: Label) => void;
  onDeselectLabel: (label: Label) => void;
  onSelectOperator: (operator: FilterOperator) => void;
  onRemove: () => void;
}) => {
  const [search, setSearch] = createSignal("");
  const filterLabelsBySearch = () => wire.labels.search(search());
  const wire = useWire();
  const operator = () => props.group.operator;
  const filters = () => props.group.filters;

  const activeOption = () => {
    const o = operator();
    if (filters().length <= 1) {
      if (isPositiveOperator(o)) {
        return "include";
      }
      return "exclude";
    }

    if (o === "and") {
      return (
        <>
          include <span class="font-bold">all</span> of
        </>
      );
    }
    if (o === "or") {
      return (
        <>
          include <span class="font-bold">any</span> of
        </>
      );
    }
    if (o === "nand") {
      return (
        <>
          exclude if <span class="font-bold">all</span>
        </>
      );
    }

    return (
      <>
        exclude if <span class="font-bold">any</span> of
      </>
    );
  };

  const options = () => {
    if (filters().length <= 1) {
      return [
        { label: "include", value: "and" },
        { label: "exclude", value: "nand" },
      ] as const;
    }
    return [
      { label: "include all of", value: "and" },
      { label: "include any of", value: "or" },
      { label: "exclude if all", value: "nand" },
      { label: "exclude if any of", value: "nor" },
    ] as const;
  };

  return (
    <Show when={filters().length > 0}>
      <div class="rounded-md border border-primary-border bg-panel flex items-center shrink-0">
        <StDropdown
          gutter={10}
          contentProps={{ class: "z-50" }}
          content={
            <div>
              <For each={options()}>
                {(opt) => (
                  <StDropdownItem
                    class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                    onClick={() => props.onSelectOperator(opt.value)}
                  >
                    {opt.label}
                  </StDropdownItem>
                )}
              </For>
            </div>
          }
        >
          <StDropdownTrigger
            onClick={() => props.onSelectOperator(operator() === "and" ? "or" : "and")}
            type="button"
            class="px-2 text-xs hover:bg-panel-hover h-6 rounded-l-md cursor-pointer"
          >
            {activeOption()}
          </StDropdownTrigger>
        </StDropdown>
        <div class="w-px h-6 bg-primary-border" />

        <StPopover
          contentProps={{ class: "z-50" }}
          placement="bottom-start"
          noArrow
          gutter={10}
          content={
            <LabelPicker
              disableCreate
              search={search()}
              setSearch={setSearch}
              labels={filterLabelsBySearch()}
              selectedLabels={props.group.filters.map((f) => f.id as LabelId) ?? []}
              onSelectLabel={props.onSelectLabel}
              onDeselectLabel={props.onDeselectLabel}
              onCreateLabel={async () => {}}
            />
          }
        >
          <StPopoverTrigger
            as="button"
            type="button"
            class="group/btn px-1 text-xs flex items-center gap-1 relative hover:bg-panel-hover h-6 cursor-pointer"
          >
            <For
              each={
                filters()
                  .filter((f) => f.kind === "label")
                  .map((f) => wire.labels.get(f.id as LabelId))
                  .filter(Boolean)
                  .slice(0, 4) as Label[]
              }
            >
              {(label, index) => (
                <div
                  class="size-4 rounded-full grid place-content-center absolute border border-muted-border group-hover/btn:scale-hover group-active/btn:scale-active transition-transform"
                  style={{
                    "background-color": label.color,
                    color: isLightColor(label.color) ? "black" : "white",
                    left: `${index() * 9 + 4}px`,
                  }}
                >
                  <Dynamic component={getIconComponent(label.icon)} class="size-3" />
                </div>
              )}
            </For>
            <span class="text-xs max-w-20 truncate" style={{ "margin-left": `${filters().length * 9 + 12}px` }}>
              {filters().length === 1
                ? wire.labels.get(filters()[0]?.id as LabelId)?.name
                : `${filters().length} labels`}
            </span>
          </StPopoverTrigger>
        </StPopover>
        <div class="w-px h-6 bg-primary-border" />
        <button
          type="button"
          aria-label="Remove filter"
          onClick={props.onRemove}
          class="group/btn px-1.5 text-xs hover:bg-panel-hover h-6 cursor-pointer rounded-r-md"
        >
          <TbX class="stroke-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
        </button>
      </div>
    </Show>
  );
};
